import React, { useEffect, useRef, useState } from "react";
import PanneauLateral from "../../common/PanneauLateral/PanneauLateral";
import { fixerQuotasOperateur } from "./operateursApi";
import {
  TERRITOIRES,
  corpsRequete,
  formaterMesures,
  idChamp,
  lireQuota,
  saisieDepuisAnnee,
  territoireSansSite,
  totalSaisie,
  validerSaisie,
} from "./quotasOperateur";

// ============================================================
// Saisie des quotas d'un financement pour une année (CE-1418, maquette 10) :
// un panneau latéral, quatre champs par territoire, pour l'accompagnement
// total et partiel ensemble, avec leur total. Un quota invalide est
// dit sous le champ, à la sortie et à l'enregistrement ; un territoire sans
// site est signalé sous son champ sans bloquer. L'enregistrement envoie
// l'année entière (l'API la remplace) : les quotas des autres financements
// inchangés, plus ceux saisis ici ; le refus de l'API s'affiche en bandeau.
// Monté à chaque ouverture (clé sur le financement) : sa saisie repart des
// quotas enregistrés.
// ============================================================

const PanneauQuotasFinancement = ({ ouvert, operateur, annee, financement, couverture, onFermer, onEnregistre }) => {
  const [saisie, setSaisie] = useState(() => saisieDepuisAnnee(annee, financement.id));
  const [erreurs, setErreurs] = useState({});
  const [refus, setRefus] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const champs = useRef({});

  // Le panneau prend le focus à l'ouverture (dialogue) ; le curseur va ensuite dans la première case.
  useEffect(() => {
    if (!ouvert) return undefined;
    const premier = champs.current[idChamp(TERRITOIRES[0].id)];
    const minuteur = setTimeout(() => premier && premier.focus(), 0);
    return () => clearTimeout(minuteur);
  }, [ouvert]);

  const saisir = (territoire, valeur) => {
    setSaisie((s) => ({ ...s, [territoire]: valeur }));
    setErreurs((e) => ({ ...e, [idChamp(territoire)]: undefined }));
  };

  const verifier = (territoire) => {
    const lu = lireQuota(saisie[territoire]);
    setErreurs((e) => ({ ...e, [idChamp(territoire)]: lu.valide ? undefined : lu.message }));
  };

  const enregistrer = () => {
    const { erreurs: enErreur, quotas } = validerSaisie(saisie, financement.id);
    const premier = Object.keys(enErreur)[0];
    if (premier) {
      setErreurs(enErreur);
      if (champs.current[premier]) champs.current[premier].focus();
      return;
    }
    setEnregistrement(true);
    setRefus(null);
    fixerQuotasOperateur(operateur.id, annee.annee, corpsRequete(annee, financement.id, quotas)).then(
      (vueAJour) => {
        setEnregistrement(false);
        onEnregistre(vueAJour);
      },
      (erreur) => {
        setEnregistrement(false);
        const champsEnErreur = (erreur && erreur.champs) || {};
        setRefus(champsEnErreur.quotas || Object.values(champsEnErreur)[0] || (erreur && erreur.message) || "Une erreur est survenue");
      }
    );
  };

  const couvertureDe = (territoire) => couverture.find((c) => c.territoire === territoire.id);

  const champ = (territoire) => {
    const id = idChamp(territoire.id);
    const erreur = erreurs[id];
    const alerte = territoireSansSite(couvertureDe(territoire), lireQuota(saisie[territoire.id]).nombre);
    const decritPar = [erreur ? `${id}-erreur` : null, alerte ? `${id}-alerte` : null].filter(Boolean).join(" ");
    return (
      <div key={territoire.id} className="panneau-quotas__ligne">
        <label htmlFor={id}>{territoire.libelle}</label>
        <div className="panneau-quotas__champ">
          <input
            ref={(el) => {
              champs.current[id] = el;
            }}
            id={id}
            name={id}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className={`form-control quotas-operateur__saisie${erreur ? " is-invalid" : ""}`}
            value={saisie[territoire.id] || ""}
            onChange={(e) => saisir(territoire.id, e.target.value)}
            onBlur={() => verifier(territoire.id)}
            aria-invalid={erreur ? "true" : undefined}
            aria-describedby={decritPar || undefined}
          />
          {erreur && (
            <small id={`${id}-erreur`} role="alert" className="quotas-operateur__erreur">
              {erreur}
            </small>
          )}
          {alerte && (
            <small id={`${id}-alerte`} className="cell-note--warn panneau-quotas__alerte">
              <i className="fa fa-exclamation-triangle" aria-hidden="true" />
              Aucun site ne dessert ce territoire
            </small>
          )}
        </div>
      </div>
    );
  };

  return (
    <PanneauLateral
      ouvert={ouvert}
      onFermer={onFermer}
      titre={`Quotas ${financement.libelle} ${annee.annee} de ${operateur.nom}`}
      surtitre={`Quotas ${annee.annee} · ${operateur.nom}`}
      className="panneau-quotas"
      pied={
        <>
          <button type="button" className="btn btn-ghost" onClick={onFermer} disabled={enregistrement}>
            Annuler
          </button>
          <button type="button" className="btn btn-primary" onClick={enregistrer} disabled={enregistrement}>
            {enregistrement ? "Enregistrement…" : `Enregistrer les quotas ${financement.libelle}`}
          </button>
        </>
      }
    >
      <h2 className="panneau-quotas__titre">
        {financement.libelle} <span className="cell-secondary">{financement.description}</span>
      </h2>
      {refus && (
        <div className="alert alert-danger" role="alert">
          {refus}
        </div>
      )}
      <fieldset className="panneau-quotas__groupe">
        <legend>Mesures par territoire</legend>
        <p className="fiche-operateur__secondaire panneau-quotas__aide">
          Un seul quota pour l'accompagnement total et l'accompagnement partiel : la part de chacun s'affiche ensuite dans le tableau.
        </p>
        <div className="panneau-quotas__lignes">{TERRITOIRES.map((territoire) => champ(territoire))}</div>
        <p className="panneau-quotas__sous-total">
          Total {financement.libelle} : <b>{formaterMesures(totalSaisie(saisie))}</b>
        </p>
      </fieldset>
      <p className="fiche-operateur__secondaire panneau-quotas__aide">
        Les communes desservies se modifient sur chaque site. Enregistrer remplace les quotas {financement.libelle} de l'année.
      </p>
    </PanneauLateral>
  );
};

export default PanneauQuotasFinancement;
